/**
 * Rating API Types
 */

export enum RatingTargetType {
  Cache = 0,
  Adventure = 1,
  POI = 2,
  Location = 3,
  Group = 4,
  User = 5,
  Discussion = 6,
  Post = 7,
}

/**
 * Rating scores explained:
 * - presenceScore: Environmental Integration - How well the location integrates into its environment
 * - obscuraScore: Educational Discovery - Educational value and unique discoveries
 * - trailcraftScore: Experience Design - Quality of route design, puzzles, and challenge progression
 * - craftmarkScore: Thematic Cohesion - Overall experience design and thematic cohesion (optional)
 */
export interface RatingDto {
  id: string
  targetType: RatingTargetType
  targetId: string
  presenceScore?: number | null // 1-5: Environmental Integration
  obscuraScore?: number | null // 1-5: Educational Discovery
  trailcraftScore?: number | null // 1-5: Experience Design
  craftmarkScore?: number | null // 1-5: Thematic Cohesion (optional)
  difficultyScore?: number | null // 1-5: User-perceived difficulty
  comment?: string | null
  containsSpoilers?: boolean
  isPrivateNote?: boolean
  userId: string
  createdAt: string
  updatedAt?: string | null
}

export interface CreateRatingRequest {
  targetType: RatingTargetType
  targetId: string
  presenceScore?: number // Optional 1-5
  obscuraScore?: number // Optional 1-5
  trailcraftScore?: number // Optional 1-5
  craftmarkScore?: number | null // Optional 1-5
  difficultyScore?: number // Optional 1-5
  comment?: string
  containsSpoilers?: boolean
  isPrivateNote?: boolean
}

export interface UpdateRatingRequest {
  presenceScore?: number
  obscuraScore?: number
  trailcraftScore?: number
  craftmarkScore?: number | null
  difficultyScore?: number
  comment?: string
  containsSpoilers?: boolean
  isPrivateNote?: boolean
}

export interface AverageRatingDto {
  averagePresenceScore: number
  averageObscuraScore: number
  averageTrailcraftScore: number
  averageCraftmarkScore?: number | null
  overallAverageScore: number
  averageDifficultyScore?: number | null
  totalRatings: number
  difficultyRatingCount?: number
  targetType: RatingTargetType
  targetId: string
  ratingDistribution?: Record<string, number>
}

/**
 * Detailed rating with user information
 */
export interface RatingDetailedDto extends RatingDto {
  user: {
    id: string
    username: string
    displayName: string
    avatarUrl?: string | null
  }
}

/**
 * Paginated response for ratings
 */
export interface PaginatedRatingsResponse<T = RatingDto> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
}

/**
 * Query params for fetching paginated ratings
 */
export interface RatingsQueryParams {
  page?: number
  pageSize?: number
  sortBy?: 'CreatedAt' | 'PresenceScore' | 'ObscuraScore' | 'TrailcraftScore' | 'DifficultyScore'
  sortOrder?: 'asc' | 'desc'
}

// Score level descriptions for tooltips
export const SCORE_LEVELS = {
  5: 'Outstanding',
  4: 'Excellent',
  3: 'Good',
  2: 'Fair',
  1: 'Poor',
} as const

// Score category info for UI display
export const RATING_CATEGORIES = {
  presence: {
    key: 'presenceScore' as const,
    label: 'Presence',
    theme: 'Environmental Integration',
    description: 'How well the location integrates into its natural or urban environment',
    icon: '✨',
    scoringGuide: {
      5: 'Outstanding - Seamlessly integrated',
      4: 'Excellent - Very well integrated',
      3: 'Good - Well integrated',
      2: 'Fair - Somewhat integrated',
      1: 'Poor - Poorly integrated',
    },
  },
  obscura: {
    key: 'obscuraScore' as const,
    label: 'Obscura',
    theme: 'Educational Discovery',
    description: 'Educational value and unique discoveries provided by the experience',
    icon: '🔮',
    scoringGuide: {
      5: 'Outstanding - Exceptional learning opportunity',
      4: 'Excellent - Strong educational value',
      3: 'Good - Moderate learning value',
      2: 'Fair - Some educational content',
      1: 'Poor - Minimal learning value',
    },
  },
  trailcraft: {
    key: 'trailcraftScore' as const,
    label: 'Trailcraft',
    theme: 'Experience Design',
    description: 'Quality of route design, puzzles, and challenge progression',
    icon: '🧭',
    scoringGuide: {
      5: 'Outstanding - Masterful design',
      4: 'Excellent - Very well designed',
      3: 'Good - Well designed',
      2: 'Fair - Adequately designed',
      1: 'Poor - Poorly designed',
    },
  },
  craftmark: {
    key: 'craftmarkScore' as const,
    label: 'Craftmark',
    theme: 'Thematic Cohesion',
    description: 'Overall experience design and thematic cohesion across all elements',
    icon: '🎨',
    optional: true,
    scoringGuide: {
      5: 'Outstanding - Perfect thematic unity',
      4: 'Excellent - Strong cohesion',
      3: 'Good - Well unified',
      2: 'Fair - Some cohesion',
      1: 'Poor - Lacking cohesion',
    },
  },
  difficulty: {
    key: 'difficultyScore' as const,
    label: 'Difficulty',
    theme: 'Challenge Level',
    description: 'How challenging was this adventure to complete',
    icon: '⚡',
    optional: true,
    scoringGuide: {
      5: 'Extreme - Very demanding',
      4: 'Difficult - Quite challenging',
      3: 'Moderate - Balanced challenge',
      2: 'Easy - Approachable',
      1: 'Beginner - Very accessible',
    },
  },
} as const
